import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { ApiVersionService } from "./api-version.service";
import { ApiUrlConfigurationService } from "../config/api-url-configuration.service";
import { Observable } from "rxjs";

/**
 * Centralized API service for Mobile Software Upgrade application.
 *
 * This service automatically resolves the correct API endpoint based on the
 * browser URL, enabling a single build to work across multiple environments.
 *
 * @example
 * // The API endpoint is automatically determined:
 * // - If browsing from dev-msu.bd.com → uses https://dev-webapi-msu.bd.com
 * // - If browsing from fvnv-msu.bd.com → uses https://fvnv-webapi-msu.bd.com
 * // - If browsing from msu.bd.com → uses https://webapi-msu.bd.com
 */
@Injectable({
  providedIn: "root"
})
export class MobileSoftwareUpgradeApiService {
  private http = inject(HttpClient);
  private versionService = inject(ApiVersionService);
  private apiUrlConfig = inject(ApiUrlConfigurationService);

  /**
   * Builds the complete API URL for a given endpoint.
   * Automatically uses the correct base URL based on the current environment.
   *
   * @param endpoint - The API endpoint path (e.g., 'users', 'devices/list')
   * @returns The complete API URL
   */
  private buildUrl(endpoint: string): string {
    const version = this.versionService.getVersion();
    const apiBaseUrl = this.apiUrlConfig.getApiEndpoint();
    return `${apiBaseUrl}/api/${version}/${endpoint}`;
  }

  /**
   * Performs a GET request to the specified endpoint.
   *
   * Overloaded to support two modes:
   *  - get<T>(endpoint) returns Observable<T> (response body only)
   *  - get<T>(endpoint, { observe: "response" }) returns Observable<HttpResponse<T>> (full response with headers)
   * We need the full-response variant so callers like CsrfService can read
   * custom response headers (e.g. "csrfrequesttoken", "cookietoken") that
   * are not part of the JSON body.
   */
  get<T>(endpoint: string): Observable<T>;
  get<T>(
    endpoint: string,
    options?: { observe?: "response"; headers?: HttpHeaders }
  ): Observable<HttpResponse<T>>;
  get<T>(
    endpoint: string,
    options?: { observe?: "body" | "response"; headers?: HttpHeaders }
  ): Observable<T | HttpResponse<T>> {
    return this.http.get<T>(this.buildUrl(endpoint), {
      observe: options?.observe ?? "body",
      headers: options?.headers
    } as object) as Observable<T | HttpResponse<T>>;
  }

  /**
   * Performs a POST request to the specified endpoint.
   */
post<T, B>(endpoint: string, body: B): Observable<T>;
post<T, B>(endpoint: string, body: B, options?:{observe?:'response', headers?:HttpHeaders}): Observable<HttpResponse<T>>;
post<T, B>(endpoint: string, body: B, options?:{observe?:'body' | 'response', headers?:HttpHeaders}): Observable<T | HttpResponse<T>>{
  return this.http.post<T>(this.buildUrl(endpoint), body, {
      observe: options?.observe ?? "body",
      headers: options?.headers
    } as object) as Observable<T | HttpResponse<T>>;
}
  /**
   * Performs a PUT request to the specified endpoint.
   */
  put<T, B>(endpoint: string, body: B): Observable<T> {
    return this.http.put<T>(this.buildUrl(endpoint), body);
  }

  /**
   * Performs a DELETE request to the specified endpoint.
   */
  delete<T>(
    endpoint: string,
    params?: HttpParams | Record<string, string | string[]>
  ): Observable<T> {
    return this.http.delete<T>(this.buildUrl(endpoint), { params });
  }
}
